import capitalize from 'lodash/capitalize'
import moment from 'moment'
import { IShipmentStopAppointmentTypes } from 'types/graphqlTypes'
import * as yup from 'yup'
import { IUseCreateShipmentFormContext } from 'apps/marketplace/pages/create-shipment/types'
import { ONE_OF_MESSAGE, REQUIRED_MESSAGE } from '../../../constants'

const INVALID_DATE_MESSAGE = 'Please enter a valid date'
const INVALID_TIME_MESSAGE = 'Please enter a valid time'
const FUTURE_DATE_MESSAGE = 'Date must be in the future'

const TIME_REGEX = /^(2[0-3]|[01]?[0-9]):([0-5]?[0-9])$/

const APPOINTMENT_TYPE_OPTIONS = Object.values(IShipmentStopAppointmentTypes)

const DateSchema = yup
  .string()
  .nullable()
  .test('date-must-be-valid', INVALID_DATE_MESSAGE, date => {
    if (!date) {
      return true
    }
    return moment(date).isValid()
  })
  .test('date-must-be-in-future', FUTURE_DATE_MESSAGE, async function isDateInFuture(date) {
    if (!date) {
      return true
    }
    const { context } = this.options as { context: IUseCreateShipmentFormContext }
    const { dateNow = '' } = context || {}
    return moment(dateNow).startOf('d').isSameOrBefore(moment(date).startOf('d'))
  })

const TimeSchema = yup
  .string()
  .notRequired()
  .nullable()
  .test('time-must-be-valid', INVALID_TIME_MESSAGE, time => {
    if (!time) {
      return true
    }
    return TIME_REGEX.test(time)
  })

export const AppointmentSchemaObject = {
  /**
   * @example 'appointment'
   */
  appointmentType: yup
    .mixed<IShipmentStopAppointmentTypes>()
    .oneOf(
      [...APPOINTMENT_TYPE_OPTIONS],
      `${ONE_OF_MESSAGE}: ${APPOINTMENT_TYPE_OPTIONS.map(val => capitalize(val)).join(', ')}`
    )
    .required(REQUIRED_MESSAGE),
  /**
   * @example '2022-10-20'
   */
  startAtDate: DateSchema.required(REQUIRED_MESSAGE),
  /**
   * @example '22:00'
   */
  startAtTime: TimeSchema.when('confirmed', {
    is: true,
    then: (schema: yup.StringSchema) => schema.required(REQUIRED_MESSAGE),
  }),
  /**
   * @example '2022-10-21'
   */
  endAtDate: DateSchema.notRequired().when('appointmentType', {
    is: IShipmentStopAppointmentTypes.range,
    then: (schema: yup.StringSchema) => schema.required(REQUIRED_MESSAGE),
  }),
  /**
   * @example '23:00'
   */
  endAtTime: TimeSchema,
  /**
   * @example false
   */
  confirmed: yup.boolean().default(false).notRequired(),
  /**
   * @example '20778799600101'
   */
  appointmentReference: yup.string().notRequired(),
  /**
   * @example '3157845898756'
   */
  confirmationReference: yup.string().notRequired(),
}

export const AppointmentSchema = yup.object(AppointmentSchemaObject)

export type TAppointmentSchema = yup.InferType<typeof AppointmentSchema>
